import { type FC, useEffect, useRef, useState } from 'react';

import Filter from '@crehana/icons/fill/Filter';
import ArrowIosDown from '@crehana/icons/outline/ArrowIosDown';
import ArrowIosUp from '@crehana/icons/outline/ArrowIosUp';
import Checkmark from '@crehana/icons/outline/Checkmark';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors } from '@crehana/ui';

import {
  FilterWrapper,
  InverseButton,
  Option,
  OptionsWrapper,
} from './FilterSelect.styles';
import type { FilterSelectProps } from './types';

const { secondaryMain } = colors;

const FilterSelect: FC<React.PropsWithChildren<FilterSelectProps>> = ({
  disabled,
  hideDirIcon,
  hint,
  icon: Icon = Filter,
  isActive = false,
  isCTA,
  isDark = false,
  options = [],
  optionSelected,
  onClick,
  onlyButton,
  rightDirection,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [selectOptions, setSelectOptions] = useState(options);
  const [label, setLabel] = useState(hint);

  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (onlyButton) setLabel(hint);
  }, [hint, onlyButton]);

  useEffect(() => {
    if (!onlyButton) {
      const onClickOutside = (e: MouseEvent) => {
        // biome-ignore lint/style/useBlockStatements: single-line early return
        if (!e.target) return;

        const target = e.target as Element;

        if (
          container.current &&
          !container.current?.contains(target) &&
          isOpen
        ) {
          setIsOpen(false);
        }
      };

      const onKeydown = (e: KeyboardEvent) => {
        // biome-ignore lint/style/useBlockStatements: single-line early return
        if (e.keyCode === 27) setIsOpen(false);
      };

      if (isOpen) {
        document.addEventListener('keydown', onKeydown);
        document.addEventListener('click', onClickOutside, true);
      } else {
        document.removeEventListener('click', onClickOutside, true);
        document.removeEventListener('keydown', onKeydown);
      }

      return () => {
        document.removeEventListener('click', onClickOutside, true);
        document.removeEventListener('keydown', onKeydown);
      };
    }
  }, [isOpen, onlyButton]);

  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (options.length) setSelectOptions(options);
  }, [options]);

  const resetOptions = () => {
    const nextOptions = [...selectOptions];

    nextOptions.map(option => (option.isChecked = false));
    if (!isCTA) {
      nextOptions[0].isChecked = true;
      setLabel(hint);
    }
    setSelectOptions(nextOptions);
    setIsOpen(false);

    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (optionSelected) optionSelected(options[0]);
  };

  const activeOption = (idx: number) => {
    const nextOptions = [...selectOptions];

    nextOptions.map(option => (option.isChecked = false));
    nextOptions[idx].isChecked = !nextOptions[idx].isChecked;
    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (!isCTA) setLabel(nextOptions[idx].labelOnActive);
    setSelectOptions(nextOptions);
    setIsOpen(false);

    // biome-ignore lint/style/useBlockStatements: single-line early return
    if (optionSelected) optionSelected(nextOptions[idx]);
  };

  const handleOption = (idx: number) => {
    const nextOptions = [...selectOptions];

    if (nextOptions[idx].isChecked) {
      resetOptions();
    } else {
      activeOption(idx);
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    onClick && onClick();
  };

  const ArrowIcon = isOpen ? ArrowIosUp : ArrowIosDown;

  return (
    <FilterWrapper ref={container}>
      <InverseButton
        isDark={isDark}
        className="font-caption"
        onClick={() => handleOpen()}
        isActive={isActive}
        disabled={disabled}
      >
        <Icon size={16} color="currentColor" />
        <span className="mx-8">{label}</span>
        {!hideDirIcon && <ArrowIcon size={16} color="currentColor" />}
      </InverseButton>
      {isOpen && (
        <OptionsWrapper
          isDark={isDark}
          className={`z-dialog`}
          isRight={rightDirection}
        >
          {selectOptions.map((option, idx) => (
            <Option
              key={option.value}
              isDark={isDark}
              className="font-body4"
              isRounded={idx + 1 === selectOptions.length}
              onClick={e => {
                e.preventDefault();
                handleOption(idx);
              }}
            >
              <p>{option.label}</p>
              {option.isChecked && !isCTA && (
                <Checkmark size={20} color={secondaryMain} />
              )}
            </Option>
          ))}
        </OptionsWrapper>
      )}
    </FilterWrapper>
  );
};

export default FilterSelect;
